import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

export interface NormalizedError {
  status: number | string
  /** Message safe to show to a customer. */
  message: string
  /** Laravel validation bag, field name -> first message. */
  fields?: Record<string, string>
  /** Machine-readable reason from `data.code`, e.g. `otp_expired`. */
  code?: string
  /** Seconds before an OTP may be requested again (`resend_in`). */
  retryAfter?: number
}

const GENERIC = 'Something went wrong. Please try again.'

/**
 * A backend can leak a raw framework or driver error straight into `message`
 * — a `SQLSTATE[...]` string, an exception class, a file path from the stack.
 * A customer can do nothing with it, and it exposes the server's internals, so
 * it must never reach the screen. This spots the shapes such a leak takes so the
 * caller can swap in a plain message instead.
 *
 * Business messages the backend writes on purpose ("We could not find that
 * appointment.") never match, so they still pass through untouched.
 */
const TECHNICAL_MARKERS = [
  'sqlstate',
  'queryexception',
  'exception',
  'stack trace',
  'sql: ',
  'pdostatement',
  'pdo::',
  '/var/www',
  '/vendor/',
  'illuminate\\',
  'symfony\\',
  'call to ',
  'undefined ',
  'syntax error',
  'fatal error',
  'unknown column',
  'integrity constraint',
]

function looksTechnical(message: string): boolean {
  const lower = message.toLowerCase()
  if (TECHNICAL_MARKERS.some((marker) => lower.includes(marker))) return true
  // A customer-facing sentence is short and single-line; a dumped error is not.
  if (message.length > 180) return true
  if (message.includes('\n')) return true
  return false
}

/**
 * True when the payload is Laravel's debug error page rather than a handled
 * response: those carry the exception class and the file/line/trace that
 * produced it. When they are present, `message` is the raw exception text and
 * must be discarded whole.
 */
function isDebugExceptionPayload(data: Record<string, unknown>): boolean {
  return (
    typeof data.exception === 'string' &&
    ('file' in data || 'line' in data || 'trace' in data)
  )
}

export function normalizeError(error: FetchBaseQueryError): NormalizedError & FetchBaseQueryError {
  const out: NormalizedError = { status: error.status, message: GENERIC }

  if (error.status === 'FETCH_ERROR') out.message = 'Cannot reach the server. Check your connection.'
  if (error.status === 'TIMEOUT_ERROR') out.message = 'The server took too long to respond.'
  if (error.status === 404) out.message = 'Not found.'

  const data = 'data' in error ? (error.data as Record<string, unknown> | undefined) : undefined

  if (data) {
    // A debug stack trace is never shown; keep the friendly default instead.
    const debugDump = isDebugExceptionPayload(data)

    if (!debugDump && typeof data.message === 'string' && data.message) {
      out.message = looksTechnical(data.message) ? GENERIC : data.message
    }

    if (debugDump || (typeof data.message === 'string' && looksTechnical(data.message))) {
      // Keep the real error where a developer can still read it.
      logSuppressed(error.status, data.message ?? data.exception)
    }

    const inner = data.data as Record<string, unknown> | undefined
    if (inner && typeof inner === 'object' && typeof inner.code === 'string') out.code = inner.code
    if (typeof data.resend_in === 'number') out.retryAfter = data.resend_in

    const errors = data.errors
    if (errors && typeof errors === 'object') {
      const fields: Record<string, string> = {}
      for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
        fields[key] = Array.isArray(value) ? String(value[0]) : String(value)
      }
      if (Object.keys(fields).length) {
        out.fields = fields
        // A 422 with a field bag reads better as the first field message.
        if (error.status === 422) out.message = Object.values(fields)[0]
      }
    }
  }

  return out as NormalizedError & FetchBaseQueryError
}

/** Surfaces the swallowed message in the dev build only — the prod build has no
 *  console to read and a customer must not see it either way. */
function logSuppressed(status: number | string, original: unknown) {
  if (import.meta.env.DEV) {
    console.warn(`[api ${status}] technical error hidden from the customer:`, original)
  }
}

/** Type guard for use inside components. */
export function isNormalizedError(e: unknown): e is NormalizedError {
  return typeof e === 'object' && e !== null && 'message' in e && 'status' in e
}
